import React, { useState, useEffect } from 'react';
import { FaGraduationCap, FaBriefcase, FaChartLine, FaGlobe, FaCheckCircle, FaClock, FaUsers, FaRocket, FaBook, FaCalendarAlt, FaBuilding, FaAward, FaStar, FaBars, FaTimes, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';

const CharteredAccountantPlatform = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const courses = [
    {
      id: 'cpa',
      title: 'Chartered Public Accountant (CPA)',
      duration: '12 Months',
      eligibility: '+2, UG, PG',
      price: '$4,999',
      description: 'Become a globally recognized CPA with comprehensive training in US GAAP, taxation, auditing, and financial reporting.',
      features: ['US CPA Certification Prep', 'International Tax Laws', 'Audit & Assurance', 'Financial Reporting', 'Real-time Projects'],
      companies: ['Deloitte', 'EY', 'PwC', 'KPMG'],
      syllabus: {
        'Month 1-2': ['Financial Accounting Fundamentals', 'US GAAP Standards', 'Accounting Principles', 'Financial Statement Analysis'],
        'Month 3-4': ['Auditing & Attestation', 'Internal Controls', 'Audit Procedures', 'Risk Assessment'],
        'Month 5-6': ['Business Environment & Concepts', 'Corporate Governance', 'Economic Concepts', 'Financial Management'],
        'Month 7-8': ['Regulation & Tax', 'Federal Taxation', 'Business Law', 'Professional Responsibilities'],
        'Month 9-10': ['Advanced Financial Accounting', 'Consolidations', 'Foreign Currency', 'Government Accounting'],
        'Month 11-12': ['CPA Exam Preparation', 'Mock Tests', 'Case Studies', 'Final Projects & Internship']
      }
    },
    {
      id: 'cfa',
      title: 'Chartered Financial Analyst (CFA)',
      duration: '9 Months',
      eligibility: 'UG, PG',
      price: '$4,499',
      description: 'Master investment analysis, portfolio management, and ethical standards to become a globally recognized CFA professional.',
      features: ['CFA Level I Prep', 'Investment Analysis', 'Portfolio Management', 'Ethics & Standards', 'Live Trading Simulation'],
      companies: ['Goldman Sachs', 'Morgan Stanley', 'JP Morgan', 'BlackRock'],
      syllabus: {
        'Month 1-2': ['Ethical & Professional Standards', 'Quantitative Methods', 'Economics', 'Financial Reporting & Analysis'],
        'Month 3-4': ['Corporate Finance', 'Equity Investments', 'Fixed Income', 'Derivatives'],
        'Month 5-6': ['Alternative Investments', 'Portfolio Management', 'Wealth Planning', 'Risk Management'],
        'Month 7-8': ['Advanced Portfolio Strategies', 'Asset Allocation', 'Performance Evaluation', 'Trading Simulations'],
        'Month 9': ['CFA Level I Exam Prep', 'Mock Exams', 'Case Studies', 'Internship at Investment Firm']
      }
    },
    {
      id: 'acca',
      title: 'ACCA (Association of Chartered Certified Accountants)',
      duration: '12 Months',
      eligibility: '+2, UG, PG',
      price: '$3,999',
      description: 'Gain UK-based professional accounting qualification recognized in 180+ countries worldwide.',
      features: ['ACCA Certification', 'IFRS Standards', 'Corporate Reporting', 'Strategic Management', 'Global Placement Support'],
      companies: ['Grant Thornton', 'BDO', 'RSM', 'Mazars'],
      syllabus: {
        'Month 1-2': ['Business & Technology', 'Management Accounting', 'Financial Accounting', 'Corporate Law'],
        'Month 3-4': ['Performance Management', 'Taxation', 'Financial Reporting', 'Audit & Assurance'],
        'Month 5-6': ['Financial Management', 'Strategic Business Leader', 'Advanced Financial Management'],
        'Month 7-8': ['Strategic Business Reporting', 'Advanced Performance Management', 'Advanced Taxation'],
        'Month 9-10': ['Advanced Audit & Assurance', 'Ethics & Professional Skills', 'Case Study Analysis'],
        'Month 11-12': ['ACCA Exam Preparation', 'Mock Tests', 'Final Project', 'MNC Internship']
      }
    },
    {
      id: 'cma',
      title: 'Certified Management Accountant (CMA)',
      duration: '6 Months',
      eligibility: 'UG, PG',
      price: '$2,999',
      description: 'Specialize in management accounting, financial planning, analysis, and strategic management for corporate roles.',
      features: ['CMA USA Certification', 'Cost Management', 'Financial Planning', 'Decision Analysis', 'Corporate Finance'],
      companies: ['IBM', 'GE', 'Siemens', 'Accenture'],
      syllabus: {
        'Month 1-2': ['Financial Planning & Analysis', 'Performance Management', 'Cost Management', 'Internal Controls'],
        'Month 3-4': ['Strategic Financial Management', 'Investment Decisions', 'Risk Management', 'Corporate Finance'],
        'Month 5-6': ['Decision Analysis', 'Professional Ethics', 'CMA Exam Prep', 'Corporate Internship Project']
      }
    }
  ];

  const weeklySchedule = {
    'Week 1': ['Orientation & Introduction', 'Course Material Distribution', 'Learning Platform Setup', 'Industry Overview Session', 'Meet Your Mentors'],
    'Week 2': ['Core Concepts - Part 1', 'Video Lectures & Reading', 'Live Q&A Sessions', 'Practice Problems', 'Weekly Assessment'],
    'Week 3': ['Core Concepts - Part 2', 'Case Study Analysis', 'Group Discussions', 'Guest Lecture from Industry Expert', 'Project Assignment'],
    'Week 4': ['Practical Applications', 'Software Tools Training', 'Real-world Scenarios', 'Mock Tests', 'Monthly Review & Feedback']
  };

  const internshipPartners = [
    { name: 'Fortune 500 Companies', logo: '🏢', count: '50+' },
    { name: 'Big 4 Accounting Firms', logo: '⭐', count: '4' },
    { name: 'Startups & Tech Companies', logo: '🚀', count: '100+' },
    { name: 'Investment Banks', logo: '💼', count: '30+' },
    { name: 'MNC Corporations', logo: '🌍', count: '200+' },
    { name: 'Consulting Firms', logo: '📊', count: '75+' }
  ];

  const benefits = [
    { icon: <FaGlobe />, title: 'International Certification', desc: 'Get globally recognized certifications' },
    { icon: <FaBriefcase />, title: 'Guaranteed Internships', desc: 'Real-time experience in top companies' },
    { icon: <FaChartLine />, title: 'Career Growth', desc: 'Fast-track your career progression' },
    { icon: <FaUsers />, title: 'Expert Mentors', desc: 'Learn from industry professionals' },
    { icon: <FaRocket />, title: 'Startup Experience', desc: 'Work with innovative startups' },
    { icon: <FaBuilding />, title: 'MNC Placement', desc: 'Opportunities in Fortune 500 companies' }
  ];

  const cfaRoadmap = [
    { phase: 'Foundation', duration: '2 Months', topics: ['Ethics & Standards', 'Quantitative Methods', 'Economics', 'Financial Reporting'] },
    { phase: 'Core Learning', duration: '3 Months', topics: ['Corporate Finance', 'Equity Investments', 'Fixed Income', 'Derivatives'] },
    { phase: 'Advanced', duration: '2 Months', topics: ['Alternative Investments', 'Portfolio Management', 'Risk Management'] },
    { phase: 'Practical', duration: '2 Months', topics: ['Live Trading', 'Case Studies', 'Mock Exams', 'Industry Project'] }
  ];

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', minHeight: '100vh' }}>
    

      {/* Hero Section */}
      <div style={{ paddingTop: '100px', paddingBottom: '60px', textAlign: 'center', color: 'white' }}>
        <div className="container">
          <h1 style={{ 
            fontSize: 'clamp(2rem, 5vw, 3.5rem)', 
            fontWeight: 'bold', 
            marginBottom: '20px',
            animation: 'fadeInUp 1s ease'
          }}>
            Launch Your International Accounting Career
          </h1>
          <p style={{ 
            fontSize: 'clamp(1rem, 2.5vw, 1.3rem)', 
            maxWidth: '800px', 
            margin: '0 auto 30px',
            animation: 'fadeInUp 1s ease 0.2s backwards'
          }}>
            Professional training for CPA, CFA, ACCA & CMA with guaranteed internships at Fortune 500, MNCs & innovative startups
          </p>
          <div style={{ animation: 'fadeInUp 1s ease 0.4s backwards' }}>
            <button 
              onClick={() => setActiveTab('courses')}
              style={{
                background: 'white',
                color: '#667eea',
                border: 'none',
                padding: '15px 40px',
                fontSize: '18px',
                borderRadius: '30px',
                fontWeight: 'bold',
                cursor: 'pointer',
                boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                transition: 'transform 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.transform = 'translateY(-3px)'}
              onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
            >
              Explore Courses <FaRocket style={{ marginLeft: '10px' }} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container pb-5">
        <div style={{ 
          background: 'white', 
          borderRadius: '20px', 
          padding: 'clamp(20px, 4vw, 40px)', 
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          minHeight: '600px'
        }}>
          
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div style={{ animation: 'fadeIn 0.5s ease' }}>
              <h2 style={{ color: '#667eea', marginBottom: '30px', fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
                <FaStar style={{ marginRight: '10px' }} />
                Why Choose CA Global Academy?
              </h2>
              
              <div className="row g-4 mb-5">
                {benefits.map((benefit, index) => (
                  <div key={index} className="col-12 col-md-6 col-lg-4">
                    <div style={{
                      padding: '25px',
                      borderRadius: '15px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      height: '100%',
                      transition: 'transform 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                    >
                      <div style={{ fontSize: '40px', marginBottom: '15px' }}>{benefit.icon}</div>
                      <h4 style={{ marginBottom: '10px' }}>{benefit.title}</h4>
                      <p style={{ margin: 0, opacity: 0.9 }}>{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <h3 style={{ color: '#667eea', marginBottom: '25px', fontSize: 'clamp(1.3rem, 2.5vw, 1.8rem)' }}>
                <FaBuilding style={{ marginRight: '10px' }} />
                Our Internship Partners
              </h3>
              <div className="row g-4">
                {internshipPartners.map((partner, index) => (
                  <div key={index} className="col-6 col-md-4 col-lg-2">
                    <div style={{
                      padding: '20px',
                      borderRadius: '12px',
                      background: '#f8f9fa',
                      textAlign: 'center',
                      transition: 'transform 0.3s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      <div style={{ fontSize: '40px', marginBottom: '10px' }}>{partner.logo}</div>
                      <h5 style={{ fontSize: 'clamp(0.8rem, 1.5vw, 1rem)', marginBottom: '5px' }}>{partner.name}</h5>
                      <p style={{ color: '#667eea', fontWeight: 'bold', margin: 0 }}>{partner.count}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Courses Tab */}
          {activeTab === 'courses' && (
            <div style={{ animation: 'fadeIn 0.5s ease' }}>
              <h2 style={{ color: '#667eea', marginBottom: '30px', fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
                <FaBook style={{ marginRight: '10px' }} />
                Our Professional Courses
              </h2>
              
              <div className="row g-4">
                {courses.map((course, index) => (
                  <div key={course.id} className="col-12 col-lg-6">
                    <div style={{
                      border: '2px solid #e0e0e0',
                      borderRadius: '15px',
                      padding: '25px',
                      height: '100%',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.borderColor = '#667eea';
                      e.currentTarget.style.boxShadow = '0 5px 20px rgba(102,126,234,0.3)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.borderColor = '#e0e0e0';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                    >
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <h3 style={{ color: '#333', fontSize: 'clamp(1.1rem, 2vw, 1.5rem)', marginBottom: '10px' }}>
                          {course.title}
                        </h3>
                        <span style={{
                          background: '#667eea',
                          color: 'white',
                          padding: '5px 15px',
                          borderRadius: '20px',
                          fontSize: '14px',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap'
                        }}>
                          {course.price}
                        </span>
                      </div>
                      
                      <div className="mb-3">
                        <span style={{ marginRight: '15px', color: '#666' }}>
                          <FaClock style={{ marginRight: '5px', color: '#667eea' }} />
                          {course.duration}
                        </span>
                        <span style={{ color: '#666' }}>
                          <FaGraduationCap style={{ marginRight: '5px', color: '#667eea' }} />
                          {course.eligibility}
                        </span>
                      </div>

                      <p style={{ color: '#666', marginBottom: '20px', fontSize: 'clamp(0.9rem, 1.5vw, 1rem)' }}>
                        {course.description}
                      </p>

                      <div className="mb-3">
                        <h5 style={{ color: '#667eea', marginBottom: '10px', fontSize: 'clamp(0.95rem, 1.8vw, 1.1rem)' }}>Key Features:</h5>
                        <div className="d-flex flex-wrap gap-2">
                          {course.features.map((feature, idx) => (
                            <span key={idx} style={{
                              background: '#f0f4ff',
                              color: '#667eea',
                              padding: '6px 12px',
                              borderRadius: '15px',
                              fontSize: 'clamp(0.75rem, 1.3vw, 0.85rem)',
                              fontWeight: '500'
                            }}>
                              <FaCheckCircle style={{ marginRight: '5px', fontSize: '12px' }} />
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="mb-3">
                        <h5 style={{ color: '#667eea', marginBottom: '10px', fontSize: 'clamp(0.95rem, 1.8vw, 1.1rem)' }}>Placement Partners:</h5>
                        <div className="d-flex flex-wrap gap-2">
                          {course.companies.map((company, idx) => (
                            <span key={idx} style={{
                              background: '#764ba2',
                              color: 'white',
                              padding: '6px 12px',
                              borderRadius: '15px',
                              fontSize: 'clamp(0.75rem, 1.3vw, 0.85rem)',
                              fontWeight: '500'
                            }}>
                              {company}
                            </span>
                          ))}
                        </div>
                      </div>

                      <button
                        onClick={() => setExpandedCourse(expandedCourse === course.id ? null : course.id)}
                        style={{
                          width: '100%',
                          background: expandedCourse === course.id ? '#667eea' : '#f0f4ff',
                          color: expandedCourse === course.id ? 'white' : '#667eea',
                          border: 'none',
                          padding: '12px',
                          borderRadius: '10px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          marginTop: '15px'
                        }}
                      >
                        {expandedCourse === course.id ? 'Hide' : 'View'} Detailed Syllabus
                        {expandedCourse === course.id ? <FaChevronUp style={{ marginLeft: '8px' }} /> : <FaChevronDown style={{ marginLeft: '8px' }} />}
                      </button>

                      {expandedCourse === course.id && (
                        <div style={{
                          marginTop: '20px',
                          padding: '20px',
                          background: '#f8f9fa',
                          borderRadius: '10px',
                          animation: 'slideDown 0.3s ease'
                        }}>
                          <h5 style={{ color: '#667eea', marginBottom: '15px' }}>Month-wise Syllabus:</h5>
                          {Object.entries(course.syllabus).map(([month, topics], idx) => (
                            <div key={idx} style={{ marginBottom: '15px' }}>
                              <h6 style={{ color: '#333', fontWeight: 'bold', marginBottom: '8px' }}>{month}</h6>
                              <ul style={{ marginBottom: 0, paddingLeft: '20px' }}>
                                {topics.map((topic, topicIdx) => (
                                  <li key={topicIdx} style={{ color: '#666', marginBottom: '5px', fontSize: 'clamp(0.85rem, 1.4vw, 0.95rem)' }}>
                                    {topic}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{
                marginTop: '40px',
                padding: '30px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '15px',
                color: 'white'
              }}>
                <h3 style={{ marginBottom: '20px', fontSize: 'clamp(1.2rem, 2.5vw, 1.7rem)' }}>
                  <FaCalendarAlt style={{ marginRight: '10px' }} />
                  Typical Weekly Schedule (Applicable to All Courses)
                </h3>
                <div className="row g-3">
                  {Object.entries(weeklySchedule).map(([week, activities], index) => (
                    <div key={index} className="col-12 col-md-6 col-lg-3">
                      <div style={{
                        background: 'rgba(255,255,255,0.15)',
                        padding: '20px',
                        borderRadius: '12px',
                        backdropFilter: 'blur(10px)',
                        height: '100%'
                      }}>
                        <h5 style={{ marginBottom: '15px', fontWeight: 'bold' }}>{week}</h5>
                        <ul style={{ margin: 0, paddingLeft: '20px', fontSize: 'clamp(0.85rem, 1.4vw, 0.95rem)' }}>
                          {activities.map((activity, idx) => (
                            <li key={idx} style={{ marginBottom: '8px', opacity: 0.95 }}>{activity}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Roadmap Tab */}
          {activeTab === 'roadmap' && (
            <div style={{ animation: 'fadeIn 0.5s ease' }}>
              <h2 style={{ color: '#667eea', marginBottom: '30px', fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
                <FaChartLine style={{ marginRight: '10px' }} />
                CFA Learning Roadmap (Sample)
              </h2>
              
              <p style={{ color: '#666', marginBottom: '30px', fontSize: 'clamp(0.95rem, 1.8vw, 1.1rem)' }}>
                Follow this structured path to master the CFA certification and launch your career in financial analysis
              </p>

              <div style={{ position: 'relative', paddingLeft: 'clamp(20px, 4vw, 40px)' }}>
                {/* Timeline Line */}
                <div style={{
                  position: 'absolute',
                  left: '15px',
                  top: '0',
                  bottom: '0',
                  width: '3px',
                  background: 'linear-gradient(to bottom, #667eea, #764ba2)',
                  display: 'none',
                  '@media (min-width: 768px)': { display: 'block' }
                }} className="d-none d-md-block" />

                {cfaRoadmap.map((phase, index) => (
                  <div key={index} style={{
                    position: 'relative',
                    marginBottom: '30px',
                    padding: '25px',
                    background: 'white',
                    border: '2px solid #e0e0e0',
                    borderRadius: '15px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.transform = 'translateX(10px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = '#e0e0e0';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                  >
                    {/* Timeline Dot */}
                    <div style={{
                      position: 'absolute',
                      left: '-35px',
                      top: '30px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      background: '#667eea',
                      border: '3px solid white',
                      boxShadow: '0 0 0 3px #667eea'
                    }} className="d-none d-md-block" />

                    <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
                      <h3 style={{ color: '#667eea', margin: 0, fontSize: 'clamp(1.1rem, 2vw, 1.5rem)' }}>
                        Phase {index + 1}: {phase.phase}
                      </h3>
                      <span style={{
                        background: '#764ba2',
                        color: 'white',
                        padding: '6px 15px',
                        borderRadius: '20px',
                        fontSize: 'clamp(0.8rem, 1.4vw, 0.9rem)',
                        fontWeight: 'bold'
                      }}>
                        {phase.duration}
                      </span>
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                      {phase.topics.map((topic, idx) => (
                        <span key={idx} style={{
                          background: '#f0f4ff',
                          color: '#667eea',
                          padding: '8px 16px',
                          borderRadius: '15px',
                          fontSize: 'clamp(0.85rem, 1.4vw, 0.95rem)',
                          fontWeight: '500'
                        }}>
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{
                marginTop: '40px',
                padding: '30px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '15px',
                color: 'white'
              }}>
                <h3 style={{ marginBottom: '20px', fontSize: 'clamp(1.2rem, 2.5vw, 1.7rem)' }}>
                  <FaAward style={{ marginRight: '10px' }} />
                  Career Progression After Certification
                </h3>
                <div className="row g-4">
                  <div className="col-12 col-md-4">
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '50px', marginBottom: '15px' }}>📊</div>
                      <h5 style={{ marginBottom: '10px' }}>Junior Analyst</h5>
                      <p style={{ margin: 0, opacity: 0.9, fontSize: 'clamp(0.85rem, 1.4vw, 0.95rem)' }}>
                        Start at $50K-$70K/year
                      </p>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '50px', marginBottom: '15px' }}>💼</div>
                      <h5 style={{ marginBottom: '10px' }}>Senior Analyst</h5>
                      <p style={{ margin: 0, opacity: 0.9, fontSize: 'clamp(0.85rem, 1.4vw, 0.95rem)' }}>
                        Grow to $80K-$120K/year
                      </p>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '50px', marginBottom: '15px' }}>🎯</div>
                      <h5 style={{ marginBottom: '10px' }}>Portfolio Manager</h5>
                      <p style={{ margin: 0, opacity: 0.9, fontSize: 'clamp(0.85rem, 1.4vw, 0.95rem)' }}>
                        Reach $150K+/year
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Internships Tab */}
          {activeTab === 'internships' && (
            <div style={{ animation: 'fadeIn 0.5s ease' }}>
              <h2 style={{ color: '#667eea', marginBottom: '30px', fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
                <FaBriefcase style={{ marginRight: '10px' }} />
                Guaranteed Internship Program
              </h2>

              <div style={{
                padding: '25px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '15px',
                color: 'white',
                marginBottom: '30px'
              }}>
                <h4 style={{ marginBottom: '15px', fontSize: 'clamp(1.1rem, 2vw, 1.4rem)' }}>100% Internship Guarantee</h4>
                <p style={{ margin: 0, fontSize: 'clamp(0.95rem, 1.8vw, 1.1rem)', lineHeight: '1.6' }}>
                  Every student is guaranteed a paid internship at leading companies. Gain real-world experience working on live projects, 
                  collaborate with industry experts, and build your professional network while you learn.
                </p>
              </div>

              <h3 style={{ color: '#667eea', marginBottom: '25px', fontSize: 'clamp(1.2rem, 2.5vw, 1.7rem)' }}>
                Internship Types & Durations
              </h3>

              <div className="row g-4 mb-5">
                <div className="col-12 col-md-4">
                  <div style={{
                    padding: '25px',
                    border: '2px solid #667eea',
                    borderRadius: '15px',
                    height: '100%',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#f0f4ff';
                    e.currentTarget.style.transform = 'translateY(-5px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                  >
                    <div style={{ fontSize: '40px', color: '#667eea', marginBottom: '15px' }}>🚀</div>
                    <h4 style={{ color: '#667eea', marginBottom: '15px', fontSize: 'clamp(1rem, 1.8vw, 1.2rem)' }}>Startup Experience</h4>
                    <p style={{ color: '#666', marginBottom: '15px', fontSize: 'clamp(0.9rem, 1.5vw, 1rem)' }}>
                      6 months with innovative startups
                    </p>
                    <ul style={{ color: '#666', paddingLeft: '20px', margin: 0, fontSize: 'clamp(0.85rem, 1.4vw, 0.95rem)' }}>
                      <li>Fast-paced environment</li>
                      <li>Multiple role exposure</li>
                      <li>Direct founder interaction</li>
                      <li>Equity opportunities</li>
                    </ul>
                  </div>
                </div>

                <div className="col-12 col-md-4">
                  <div style={{
                    padding: '25px',
                    border: '2px solid #667eea',
                    borderRadius: '15px',
                    height: '100%',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#f0f4ff';
                    e.currentTarget.style.transform = 'translateY(-5px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                  >
                    <div style={{ fontSize: '40px', color: '#667eea', marginBottom: '15px' }}>🏢</div>
                    <h4 style={{ color: '#667eea', marginBottom: '15px', fontSize: 'clamp(1rem, 1.8vw, 1.2rem)' }}>Fortune 500</h4>
                    <p style={{ color: '#666', marginBottom: '15px', fontSize: 'clamp(0.9rem, 1.5vw, 1rem)' }}>
                      9-12 months with top corporations
                    </p>
                    <ul style={{ color: '#666', paddingLeft: '20px', margin: 0, fontSize: 'clamp(0.85rem, 1.4vw, 0.95rem)' }}>
                      <li>Structured training programs</li>
                      <li>Global project exposure</li>
                      <li>Mentorship from leaders</li>
                      <li>Full-time conversion potential</li>
                    </ul>
                  </div>
                </div>

                <div className="col-12 col-md-4">
                  <div style={{
                    padding: '25px',
                    border: '2px solid #667eea',
                    borderRadius: '15px',
                    height: '100%',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#f0f4ff';
                    e.currentTarget.style.transform = 'translateY(-5px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                  >
                    <div style={{ fontSize: '40px', color: '#667eea', marginBottom: '15px' }}>⭐</div>
                    <h4 style={{ color: '#667eea', marginBottom: '15px', fontSize: 'clamp(1rem, 1.8vw, 1.2rem)' }}>Big 4 Firms</h4>
                    <p style={{ color: '#666', marginBottom: '15px', fontSize: 'clamp(0.9rem, 1.5vw, 1rem)' }}>
                      12 months with elite firms
                    </p>
                    <ul style={{ color: '#666', paddingLeft: '20px', margin: 0, fontSize: 'clamp(0.85rem, 1.4vw, 0.95rem)' }}>
                      <li>Premium audit experience</li>
                      <li>Client-facing projects</li>
                      <li>Professional certification support</li>
                      <li>International opportunities</li>
                    </ul>
                  </div>
                </div>
              </div>

              <h3 style={{ color: '#667eea', marginBottom: '25px', fontSize: 'clamp(1.2rem, 2.5vw, 1.7rem)' }}>
                Real-Time Project Experience
              </h3>

              <div className="row g-4 mb-5">
                <div className="col-12 col-md-6">
                  <div style={{
                    padding: '20px',
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    borderLeft: '4px solid #667eea'
                  }}>
                    <h5 style={{ color: '#667eea', marginBottom: '15px', fontSize: 'clamp(1rem, 1.8vw, 1.2rem)' }}>
                      <FaChartLine style={{ marginRight: '8px' }} />
                      Financial Analysis Projects
                    </h5>
                    <ul style={{ color: '#666', margin: 0, paddingLeft: '20px', fontSize: 'clamp(0.85rem, 1.4vw, 0.95rem)' }}>
                      <li>Quarterly earnings analysis for listed companies</li>
                      <li>Investment portfolio management</li>
                      <li>Risk assessment and modeling</li>
                      <li>M&A valuation studies</li>
                    </ul>
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <div style={{
                    padding: '20px',
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    borderLeft: '4px solid #764ba2'
                  }}>
                    <h5 style={{ color: '#764ba2', marginBottom: '15px', fontSize: 'clamp(1rem, 1.8vw, 1.2rem)' }}>
                      <FaBook style={{ marginRight: '8px' }} />
                      Audit & Compliance Work
                    </h5>
                    <ul style={{ color: '#666', margin: 0, paddingLeft: '20px', fontSize: 'clamp(0.85rem, 1.4vw, 0.95rem)' }}>
                      <li>Internal audit procedures</li>
                      <li>SOX compliance testing</li>
                      <li>Financial statement audits</li>
                      <li>Process improvement recommendations</li>
                    </ul>
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <div style={{
                    padding: '20px',
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    borderLeft: '4px solid #667eea'
                  }}>
                    <h5 style={{ color: '#667eea', marginBottom: '15px', fontSize: 'clamp(1rem, 1.8vw, 1.2rem)' }}>
                      <FaGlobe style={{ marginRight: '8px' }} />
                      International Tax Planning
                    </h5>
                    <ul style={{ color: '#666', margin: 0, paddingLeft: '20px', fontSize: 'clamp(0.85rem, 1.4vw, 0.95rem)' }}>
                      <li>Transfer pricing documentation</li>
                      <li>Cross-border tax strategies</li>
                      <li>Tax compliance for multinationals</li>
                      <li>BEPS implementation projects</li>
                    </ul>
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <div style={{
                    padding: '20px',
                    background: '#f8f9fa',
                    borderRadius: '12px',
                    borderLeft: '4px solid #764ba2'
                  }}>
                    <h5 style={{ color: '#764ba2', marginBottom: '15px', fontSize: 'clamp(1rem, 1.8vw, 1.2rem)' }}>
                      <FaRocket style={{ marginRight: '8px' }} />
                      Strategic Consulting
                    </h5>
                    <ul style={{ color: '#666', margin: 0, paddingLeft: '20px', fontSize: 'clamp(0.85rem, 1.4vw, 0.95rem)' }}>
                      <li>Business strategy development</li>
                      <li>Cost optimization initiatives</li>
                      <li>Financial modeling for growth</li>
                      <li>Due diligence for acquisitions</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div style={{
                padding: '30px',
                background: 'linear-gradient(135deg, #f0f4ff 0%, #e6e9ff 100%)',
                borderRadius: '15px',
                border: '2px solid #667eea'
              }}>
                <h4 style={{ color: '#667eea', marginBottom: '20px', fontSize: 'clamp(1.1rem, 2vw, 1.4rem)' }}>
                  <FaUsers style={{ marginRight: '10px' }} />
                  Student Success Stories
                </h4>
                <div className="row g-4">
                  <div className="col-12 col-md-4">
                    <div style={{
                      padding: '20px',
                      background: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ fontSize: '30px', marginBottom: '10px' }}>👨‍💼</div>
                      <h5 style={{ color: '#333', marginBottom: '10px', fontSize: 'clamp(0.95rem, 1.6vw, 1.1rem)' }}>Rahul Sharma</h5>
                      <p style={{ color: '#667eea', fontWeight: 'bold', marginBottom: '10px', fontSize: 'clamp(0.85rem, 1.4vw, 0.95rem)' }}>
                        CPA Graduate → Deloitte
                      </p>
                      <p style={{ color: '#666', margin: 0, fontSize: 'clamp(0.8rem, 1.3vw, 0.9rem)', fontStyle: 'italic' }}>
                        "The internship gave me hands-on experience with Fortune 500 clients. Now I'm a full-time auditor!"
                      </p>
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div style={{
                      padding: '20px',
                      background: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ fontSize: '30px', marginBottom: '10px' }}>👩‍💼</div>
                      <h5 style={{ color: '#333', marginBottom: '10px', fontSize: 'clamp(0.95rem, 1.6vw, 1.1rem)' }}>Priya Desai</h5>
                      <p style={{ color: '#667eea', fontWeight: 'bold', marginBottom: '10px', fontSize: 'clamp(0.85rem, 1.4vw, 0.95rem)' }}>
                        CFA Graduate → Goldman Sachs
                      </p>
                      <p style={{ color: '#666', margin: 0, fontSize: 'clamp(0.8rem, 1.3vw, 0.9rem)', fontStyle: 'italic' }}>
                        "Working on live portfolios during internship prepared me perfectly for my analyst role."
                      </p>
                    </div>
                  </div>

                  <div className="col-12 col-md-4">
                    <div style={{
                      padding: '20px',
                      background: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ fontSize: '30px', marginBottom: '10px' }}>👨‍💼</div>
                      <h5 style={{ color: '#333', marginBottom: '10px', fontSize: 'clamp(0.95rem, 1.6vw, 1.1rem)' }}>Amit Patel</h5>
                      <p style={{ color: '#667eea', fontWeight: 'bold', marginBottom: '10px', fontSize: 'clamp(0.85rem, 1.4vw, 0.95rem)' }}>
                        ACCA Graduate → EY
                      </p>
                      <p style={{ color: '#666', margin: 0, fontSize: 'clamp(0.8rem, 1.3vw, 0.9rem)', fontStyle: 'italic' }}>
                        "The startup internship taught me agility. Now I handle multiple client portfolios at EY."
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* CTA Section */}
        <div style={{
          marginTop: '40px',
          padding: 'clamp(30px, 5vw, 50px)',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '20px',
          textAlign: 'center',
          color: 'white',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        }}>
          <h2 style={{ marginBottom: '20px', fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', fontWeight: 'bold' }}>
            Ready to Launch Your Global Career?
          </h2>
          <p style={{ marginBottom: '30px', fontSize: 'clamp(1rem, 2vw, 1.3rem)', maxWidth: '800px', margin: '0 auto 30px' }}>
            Join thousands of students who have transformed their careers with our internationally recognized certifications and guaranteed internships.
          </p>
          <div className="d-flex flex-wrap justify-content-center gap-3">
            <button style={{
              background: 'white',
              color: '#667eea',
              border: 'none',
              padding: '15px 35px',
              fontSize: 'clamp(1rem, 1.8vw, 1.1rem)',
              borderRadius: '30px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'transform 0.3s ease',
              boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-3px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
            >
              Apply Now <FaRocket style={{ marginLeft: '8px' }} />
            </button>
            <button style={{
              background: 'transparent',
              color: 'white',
              border: '2px solid white',
              padding: '15px 35px',
              fontSize: 'clamp(1rem, 1.8vw, 1.1rem)',
              borderRadius: '30px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'transform 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-3px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
            >
              Download Brochure <FaBook style={{ marginLeft: '8px' }} />
            </button>
          </div>

          <div className="row g-4 mt-4" style={{ maxWidth: '900px', margin: '40px auto 0' }}>
            <div className="col-6 col-md-3">
              <div style={{ fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', fontWeight: 'bold' }}>5000+</div>
              <div style={{ fontSize: 'clamp(0.85rem, 1.4vw, 1rem)', opacity: 0.9 }}>Students Trained</div>
            </div>
            <div className="col-6 col-md-3">
              <div style={{ fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', fontWeight: 'bold' }}>95%</div>
              <div style={{ fontSize: 'clamp(0.85rem, 1.4vw, 1rem)', opacity: 0.9 }}>Placement Rate</div>
            </div>
            <div className="col-6 col-md-3">
              <div style={{ fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', fontWeight: 'bold' }}>200+</div>
              <div style={{ fontSize: 'clamp(0.85rem, 1.4vw, 1rem)', opacity: 0.9 }}>Partner Companies</div>
            </div>
            <div className="col-6 col-md-3">
              <div style={{ fontSize: 'clamp(1.8rem, 3vw, 2.5rem)', fontWeight: 'bold' }}>50+</div>
              <div style={{ fontSize: 'clamp(0.85rem, 1.4vw, 1rem)', opacity: 0.9 }}>Countries</div>
            </div>
          </div>
        </div>
      </div>


      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideDown {
          from {
            opacity: 0;
            max-height: 0;
          }
          to {
            opacity: 1;
            max-height: 2000px;
          }
        }
        * {
          box-sizing: border-box;
        }
      `}</style>
    </div>
  );
};

export default CharteredAccountantPlatform;